import { memo, useMemo } from 'react';
import { HStack, VStack } from '@cbhq/cds-web/layout';
import { useBreakpoints } from '@cbhq/cds-web/hooks/useBreakpoints';
import { TextTitle3, TextTitle1, TextHeadline, TextTitle4 } from '@cbhq/cds-web/typography';
import { SummaryCard } from '@/components/common/SummaryCard';
import { usePositions } from '@/onchain/hooks/usePositions';
import { DisplayType, format } from '@/utils/numberFormatter';

export const TangorPoolsOverview = memo(function TangorPoolsOverview() {
  const { isPhone, isPhoneLandscape } = useBreakpoints();
  const isMobile = isPhone || isPhoneLandscape;
  const { positions } = usePositions();
  const data = useMemo(
    () => ({
      availableFees: positions.reduce(
        (sum, position) => sum + position.totalUnclaimedFeesNotional,
        0,
      ),
      feesChangePercent: 0,
      numberOfPositions: positions.length,
    }),
    [positions],
  );
  const isPositiveChange = data.feesChangePercent >= 0;

  const header = <TextTitle3 as="h2">Overview</TextTitle3>;

  const statsContent = (
    <>
      {/* Available fees */}
      <VStack alignItems="flex-start" gap={0.5}>
        <TextHeadline as="span" color="fgMuted">
          Available fees
        </TextHeadline>
        <HStack alignItems="baseline" gap={1}>
          <TextTitle1 as="span">
            {format({ value: data.availableFees, type: DisplayType.Fiat })}
          </TextTitle1>
          <TextTitle4 as="span" color={isPositiveChange ? 'fgPositive' : 'fgNegative'}>
            {isPositiveChange ? '↗' : '↘'}{' '}
            {format({
              value: Math.abs(data.feesChangePercent / 100),
              type: DisplayType.Percentage,
            })}
          </TextTitle4>
        </HStack>
      </VStack>

      {/* Number of positions */}
      <VStack alignItems="flex-start" gap={0.5}>
        <TextHeadline as="span" color="fgMuted">
          Number of positions
        </TextHeadline>
        <TextTitle1 as="span">{data.numberOfPositions}</TextTitle1>
      </VStack>
    </>
  );

  return (
    <SummaryCard header={header} testID="tangor-pools-overview">
      {isMobile ? (
        <VStack paddingX={3} paddingBottom={2} gap={3} alignItems="flex-start" width="100%">
          {statsContent}
        </VStack>
      ) : (
        <HStack paddingX={3} paddingBottom={2} gap={8} alignItems="flex-start">
          {statsContent}
        </HStack>
      )}
    </SummaryCard>
  );
});
